import React, { useState } from "react";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DollarSign, Plus, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useCurrentBusiness } from "@/hooks/use-current-business";
import { SubscriptionRequired } from "@/components/ui/subscription-required";
import { CreateBillPaymentDialog } from "@/components/expenses/create-bill-payment-dialog";
import { useBillPayments } from "@/hooks/use-bill-payments";

export default function BillPaymentsPage() {
  const [isCreateBillPaymentOpen, setIsCreateBillPaymentOpen] = useState(false);
  const [billId, setBillId] = useState<number | undefined>();
  const { toast } = useToast();
  const { currentBusiness: selectedBusiness } = useCurrentBusiness();
  // Get bill payments for a specific bill if billId is provided
  const { payments: billPayments = [], isLoading } = useBillPayments(billId);
  
  const handleRefresh = () => {
    window.location.reload();
  };

  if (!selectedBusiness) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh] flex-col">
          <DollarSign className="h-16 w-16 text-gray-300 mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">No Business Selected</h2>
          <p className="text-gray-600 mb-4">Please select or create a business to manage bill payments.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      
      <div className="px-4 py-6 sm:px-6 lg:px-8">
        {/* Page header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Bill Payments</h1>
            <p className="mt-1 text-sm text-neutral-500">
              Track and record payments for your bills
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex space-x-3">
            <Button variant="outline" onClick={handleRefresh}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={() => setIsCreateBillPaymentOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Record Payment
            </Button>
          </div>
        </div>

        {/* Bill Payments tabs */}
        <Tabs defaultValue="all" className="mt-6">
          <TabsList className="mb-6">
            <TabsTrigger value="all">All Payments</TabsTrigger>
            <TabsTrigger value="recent">Recent</TabsTrigger>
            <TabsTrigger value="scheduled">Scheduled</TabsTrigger>
          </TabsList>
          
          <TabsContent value="all">
            <Card>
              <CardHeader>
                <CardTitle>All Bill Payments</CardTitle>
                <CardDescription>View all your recorded bill payments</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
                  </div>
                ) : !billPayments || billPayments.length === 0 ? (
                  <div className="flex items-center justify-center h-64 border border-dashed rounded-lg">
                    <div className="text-center">
                      <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900">No bill payments recorded</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Record your first bill payment to track your expenses
                      </p>
                      <div className="mt-6">
                        <Button onClick={() => setIsCreateBillPaymentOpen(true)}>
                          <Plus className="h-4 w-4 mr-2" />
                          Record Payment
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b">
                          <th className="py-2 px-4 text-left font-medium text-gray-500">Date</th>
                          <th className="py-2 px-4 text-left font-medium text-gray-500">Bill ID</th>
                          <th className="py-2 px-4 text-left font-medium text-gray-500">Amount</th>
                          <th className="py-2 px-4 text-left font-medium text-gray-500">Method</th>
                          <th className="py-2 px-4 text-left font-medium text-gray-500">Reference</th>
                          <th className="py-2 px-4 text-left font-medium text-gray-500">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {billPayments.map((payment) => (
                          <tr key={payment.id} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-4">{payment.date ? new Date(payment.date).toLocaleDateString() : '-'}</td>
                            <td className="py-3 px-4">{payment.billId}</td>
                            <td className="py-3 px-4 font-medium">${parseFloat(payment.amount).toFixed(2)}</td>
                            <td className="py-3 px-4">{payment.paymentMethod || '-'}</td>
                            <td className="py-3 px-4">{payment.reference || '-'}</td>
                            <td className="py-3 px-4">
                              <Button variant="ghost" size="sm">View</Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Other tab contents */}
          <TabsContent value="recent">
            <Card>
              <CardHeader>
                <CardTitle>Recent Payments</CardTitle>
                <CardDescription>Payments made in the last 30 days</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-center h-64 border border-dashed rounded-lg">
                  <div className="text-center">
                    <p className="text-gray-500">No recent bill payments found</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Similar for other tabs */}
        </Tabs>
      </div>

      {/* Bill Payment Dialog */}
      {selectedBusiness && (
        <CreateBillPaymentDialog
          isOpen={isCreateBillPaymentOpen}
          onClose={() => setIsCreateBillPaymentOpen(false)}
          businessId={selectedBusiness.id}
        />
      )}
    </Layout>
  );
}